// src/hooks/useAdminFlags.ts
import { useEffect, useState } from "react";
import { apiGetProfile, apiAdminWhoAmI } from "api/client";

/**
 * Centralized source of admin/superadmin flags.
 * Prefers flags on /users/me/profile (if backend includes them).
 * Falls back to /admin/whoami.
 */
export function useAdminFlags() {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const profile = await apiGetProfile();
        const viaProfile =
          profile && (profile as any)?.is_admin !== undefined && (profile as any)?.is_superadmin !== undefined;

        if (viaProfile) {
          if (!mounted) return;
          setIsAdmin(!!(profile as any).is_admin);
          setIsSuperadmin(!!(profile as any).is_superadmin);
          setLoading(false);
          return;
        }

        // Fallback to whoami if flags not on profile
        const who = await apiAdminWhoAmI();
        if (!mounted) return;
        setIsAdmin(who.is_admin === true);
        setIsSuperadmin(who.is_superadmin === true);
      } catch (e: any) {
        if (!mounted) return;
        // Treat any error as "not admin" but record message for debugging if needed
        setIsAdmin(false);
        setIsSuperadmin(false);
        setError(e?.message || null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  return { loading, isAdmin, isSuperadmin, error };
}
